'use strict';

const alertMessage = require('../utils/alertMessage');
const autocompleteUtils = require('../autocomplete/autocompleteUtils');

const SELECTORS = {
    element: '.js-pickup-autocomplete',
    storesList: '.js-pickup-stores-list',
    useCurrentLocation: '.js-use-customer-location',
    selectStoreBtn: '.js-select-store',
    storeItem: '.js-store-item',
    selectedStoreInput: 'input#selectedStore',
    selectedStoreName: 'input#selectedStoreName',
    selectedStoreEmail: 'input#selectedStoreEmail',
    storeCard: '.js-store-card',
    storeCardName: '.js-store-card-name',
    storeCardAddress: '.js-store-card-address',
    storeCardPhone: '.js-store-card-phone',
    shippingStoreBlock: '.js-shipping-store-block',
    defaultLng: '#defaultLng',
    pickupStoreSearch: '#pickupStoreSearch',
    pickupStoreModal: '#pickupStoreModalTitle'
};

/**
 * PickupInStore class that handles the address autocomplete components
 */
class PickupInStore {
    constructor(element) {
        this.element = element;
        this.form = this.element.closest('form');
        this.useCurrentLocation = this.form.find(SELECTORS.useCurrentLocation);
        this.storesList = $(SELECTORS.storesList);
        this.autocomplete = null;
        this.nodeElement = element[0];
        this.$suggestionsContainer = null;
        this.hideSuggestions = null;
        this.init();
    }

    /**
     * Initialize the address autocomplete component
     * @memberof PickupInStore
     * @method init
     * @public
     * @returns {void} 
     */
    init() {
        // Use autocomplete utility for initialization
        autocompleteUtils.initializeGoogleMapsAutocomplete(() => {
            this.initializeAutocomplete();
        });

        this.form.on('submit', (e) => {
            e.preventDefault();
            const address = this.nodeElement.value;
            this.getLatLng(address);
        });

        $(SELECTORS.pickupStoreSearch).on('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                if (this.$suggestionsContainer && this.$suggestionsContainer.is(':visible')) {
                    if (this.hideSuggestions) {
                        this.hideSuggestions();
                    }
                }
                
                const address = this.nodeElement.value;
                this.getLatLng(address);
            }
        });

        this.useCurrentLocation.on('click', (e) => {
            e.preventDefault();
            this.useCurrentLocationHandler();
        });

        $('body').on('click', SELECTORS.selectStoreBtn, (evt) => {
            evt.preventDefault();
            this.selectStoreHandler(evt.currentTarget);
        });
    }

    // Removed getGoogleApiKey() and isGoogleApiKeyValid() - now using autocompleteUtils

    /**
     * Initialize the address autocomplete using new utility system
     * @memberof PickupInStore
     * @method initializeAutocomplete
     * @public
     * @returns {void}
     */
    async initializeAutocomplete() {
        if (!this.nodeElement) {
            console.error(`No element found.`);
            return;
        }

        await this.setupCustomAutocomplete();
    }

    /**
     * Setup custom autocomplete using utility functions
     * @memberof PickupInStore
     * @method setupCustomAutocomplete
     * @public
     * @returns {Promise<void>}
     */
    async setupCustomAutocomplete() {
        // Import Google Maps libraries
        const { AutocompleteSuggestion } = await google.maps.importLibrary("places");
        
        const $container = $(this.element).closest('.js-wrap-autocomplete');
        const $input = $(this.element);
        
        // Create autocomplete container using utility
        let $suggestionsContainer = $container.find('.js-suggestions-container');
        if ($suggestionsContainer.length === 0) {
            const containerElements = autocompleteUtils.createAutocompleteContainer($container, $input);
            $suggestionsContainer = containerElements.$container;
        }

        // Save reference to suggestions container for external use
        this.$suggestionsContainer = $suggestionsContainer;

        // Create live region for announcements
        const $liveRegion = autocompleteUtils.createLiveRegion($container);

        // Variables for managing suggestions
        let currentSessionToken = null;
        let debounceTimeout = null;
        let currentSuggestions = [];

        // Function to hide suggestions
        const hideSuggestions = () => {
            if ($suggestionsContainer.is(':visible')) {
                $suggestionsContainer.hide().empty();
                autocompleteUtils.updateAutocompleteState($input, { expanded: false });
                currentSuggestions = [];
                $input.trigger('focus');
            }
        };

        // Save reference to hideSuggestions for external use
        this.hideSuggestions = hideSuggestions;

        // Function to show suggestions
        const showSuggestions = (suggestions) => {
            $suggestionsContainer.empty();
            currentSuggestions = suggestions;

            if (suggestions.length === 0) {
                hideSuggestions();
                return;
            }

            // Create suggestion items using utility
            autocompleteUtils.createSuggestionItems(
                suggestions,
                $suggestionsContainer,
                {
                    onSelect: async (suggestion, displayText) => {
                        try {
                            // Get place data for store location search
                            const place = await autocompleteUtils.fetchPlaceFromPrediction(
                                suggestion.placePrediction,
                                {
                                    includeGeometry: true,
                                    includeAddress: false,
                                    includeFormatted: false,
                                    includeDisplay: false
                                }
                            );

                            // Update input with selected address
                            $input.val(displayText);
                            hideSuggestions();

                            // Perform store search with coordinates
                            if (place.location) {
                                const lat = place.location.lat();
                                const lng = place.location.lng();
                                this.performAjaxRequest(lat, lng);
                            }
                        } catch (error) {
                            console.error('Error processing place selection:', error);
                        }
                    },
                    onHover: ($suggestionItem, optionId) => {
                        $suggestionsContainer.find('.cc-suggestion-item').removeClass('cc-focus').attr('aria-selected', 'false');
                        $suggestionItem.addClass('cc-focus').attr('aria-selected', 'true');
                        autocompleteUtils.updateAutocompleteState($input, { 
                            expanded: true,
                            activeDescendant: optionId
                        });
                    }
                }
            );

            autocompleteUtils.updateAutocompleteState($input, { expanded: true });
            $suggestionsContainer.show();
            autocompleteUtils.announceResults($liveRegion, suggestions.length, { $container: $container });
        };

        // Input event listener with debouncing
        $input.on("input", async (event) => {
            const inputValue = event.target.value.trim();

            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }

            if (inputValue.length < 2) {
                hideSuggestions();
                return;
            }

            debounceTimeout = setTimeout(async () => {
                try {
                    currentSessionToken = new google.maps.places.AutocompleteSessionToken();

                    const request = {
                        input: inputValue,
                        sessionToken: currentSessionToken,
                        includedRegionCodes: ["IT"],
                        locationBias: {
                          center: { lat: 41.9028, lng: 12.4964 },
                          radius: 50000
                        },
                    };

                    const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
                    showSuggestions(suggestions);

                } catch (error) {
                    console.error("Error fetching store location suggestions:", error);
                    hideSuggestions();
                }
            }, 300);
        });

        // Keyboard navigation
        $input.on("keydown", (event) => {
            autocompleteUtils.handleKeyboardNavigation(event, {
                $input: $input,
                $container: $container,
                $suggestionsContainer: $suggestionsContainer,
                currentSuggestions: currentSuggestions,
                handleSelection: async (suggestion, inputValue) => {
                    try {
                        const place = await autocompleteUtils.fetchPlaceFromPrediction(
                            suggestion.placePrediction,
                            { includeGeometry: true }
                        );
                        
                        if (place.location) {
                            const lat = place.location.lat();
                            const lng = place.location.lng();
                            this.performAjaxRequest(lat, lng);
                        }
                    } catch (error) {
                        console.error('Error processing keyboard selection:', error);
                    }
                },
                hideSuggestions: hideSuggestions
            });
        });

        // Hide suggestions when clicking outside
        $(document).on("click", (event) => {
            if (!$container.is(event.target) && $container.has(event.target).length === 0) {
                hideSuggestions();
            }
        });

        // Change event listener
        $input.on('change', (e) => {
            const targetValue = e.currentTarget.value;
            if (targetValue.trim() === "") {
                hideSuggestions();
            }
        });
    }

    /**
     * Get latitude and longitude from address
     * @memberof PickupInStore
     * @method getLatLng
     * @public
     * @param {Object} address - Address object
     */
    getLatLng(address) {
        const geocoder = new google.maps.Geocoder();

        geocoder.geocode({ 'address': address }, (results, status) => {
            let lat, lng;
            if (status === 'OK') {
                if (results[0]) {
                    lat = results[0].geometry.location.lat();
                    lng = results[0].geometry.location.lng();
                } else {
                    console.error('No result found.');
                }
            } else {
                console.error('Geocoder failed: ' + status);
            }
            this.performAjaxRequest(lat, lng);
        });
    }

    /**
     * Perform AJAX request to get stores list
     * @memberof PickupInStore
     * @method performAjaxRequest
     * @public
     * @param {*} lat - Latitude
     * @param {*} lng - Longitude
     * @returns 
     */
    performAjaxRequest(lat, lng) {
        const data = {
            lat: lat,
            lng: lng
        };

        const form = this.element.closest('form');
        const url = form.attr('action');

        $.ajax({
            url: url,
            type: 'GET',
            data: data,
            success: (response) => {
                if (response.success) {
                    this.storesList.html(response.template);
                } else {
                    alertMessage(response.message, 'cc-alert--danger');
                    this.storesList.html("");
                }
            },
            error: (err) => {
                alertMessage(err.message, 'cc-alert--danger');
            },
        });

        $.spinner().stop();
    }

    /**
     * Use current location handler to get the user location
     * @memberof PickupInStore
     * @method useCurrentLocationHandler
     * @public
     * @returns {void}
     */
    useCurrentLocationHandler() {
        if (!navigator.geolocation) {
            console.error('Geolocation is not supported by your browser.');
            return;
        }

        navigator.geolocation.getCurrentPosition((position) => {
            const geocoder = new google.maps.Geocoder();
            const latlng = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            };

            geocoder.geocode({ 'location': latlng }, (results, status) => {
                if (status === 'OK') {
                    if (results[0]) {
                        this.nodeElement.value = results[0].formatted_address;
                        this.form.submit();
                    } else {
                        console.error('No results found');
                        $.spinner().stop();
                    }
                } else {
                    console.error('Geocoder failed due to: ' + status);
                    $.spinner().stop();
                }
            });
        }, () => {
            console.error('Unable to retrieve your location');
            $.spinner().stop();
        });
    }

    /**
     * Select store handler to select the store
     * @memberof PickupInStore
     * @method selectStoreHandler
     * @public
     * @param {HTMLElement} target - Target element
     * @returns 
     */
    selectStoreHandler(target) {
        const $target = $(target);
        const errorMsg = $target.data('error-message');
        const storeCard = $target.closest(SELECTORS.storeItem);
        const storeData = JSON.parse(storeCard.attr('data-store'));

        if (!storeData) {
            alertMessage(errorMsg, 'cc-alert--danger');
            return;
        }

        this.element.val('');
        this.storesList.html('');

        $('body').trigger('shipping:storeSelected', storeData);

        $(SELECTORS.selectedStoreInput).val(storeData.ID);
        $(SELECTORS.selectedStoreName).val(storeData.name);
        $(SELECTORS.selectedStoreEmail).val(storeData.email);
        $(SELECTORS.storeCardName).text(storeData.name);
        $(SELECTORS.storeCardAddress).text(storeData.formattedAddress);
        $(SELECTORS.storeCardPhone).text(storeData.phone);
        $(SELECTORS.storeCard).attr('data-store', storeCard.attr('data-store'));
        $(SELECTORS.shippingStoreBlock).removeClass('d-none');
        $(SELECTORS.pickupStoreModal).addClass('d-none');
    }
}

module.exports = () => {
    $(SELECTORS.element).each((index, element) => {
        new PickupInStore($(element));
    });
};
